export type BrandPrintLanguage = "lo" | "en";

export type BrandPrintUrlOptions = {
  lang?: BrandPrintLanguage;
  /** Include studio stamp/signature image (default true). */
  includeSignature?: boolean;
  /** Alias of includeSignature — studio stamp/signature image on the right block. */
  includeStudioSignature?: boolean;
  /** Studio stamp image (combined asset when only one file exists). */
  includeStudioStamp?: boolean;
  /** Rehearsal schedule print layout (default day list). */
  layout?: "day" | "matrix";
};

export function parseBrandPrintLanguage(value: unknown): BrandPrintLanguage {
  return value === "en" ? "en" : "lo";
}

/** `signature=0|false|no` → false; `signature=1|true|yes` → true */
export function parseBrandPrintIncludeSignature(value: unknown): boolean {
  if (value === "0" || value === "false" || value === "no") return false;
  if (value === "1" || value === "true" || value === "yes") return true;
  return true;
}

export function buildBrandPrintLanguageSwitchUrls(
  pathname: string,
  currentSearch?: string | URLSearchParams,
): {
  lo: string;
  en: string;
} {
  const base = pathname.split("?")[0] ?? pathname;
  const params = new URLSearchParams(
    typeof currentSearch === "string"
      ? currentSearch.startsWith("?")
        ? currentSearch.slice(1)
        : currentSearch
      : (currentSearch ?? undefined),
  );
  params.delete("lang");

  const withLang = (lang: BrandPrintLanguage) => {
    const next = new URLSearchParams(params);
    next.set("lang", lang);
    const qs = next.toString();
    return qs ? `${base}?${qs}` : `${base}?lang=${lang}`;
  };

  return {
    lo: withLang("lo"),
    en: withLang("en"),
  };
}

export function withBrandPrintLanguage(
  url: string,
  lang: BrandPrintLanguage,
  options?: Omit<BrandPrintUrlOptions, "lang">,
): string {
  const [path, existingQs = ""] = url.split("?");
  const params = new URLSearchParams(existingQs);
  params.set("lang", lang);
  const includeStudioSignature =
    options?.includeStudioSignature ?? options?.includeSignature;
  const includeStudioStamp = options?.includeStudioStamp;
  if (includeStudioSignature === false) {
    params.set("signature", "0");
  } else if (includeStudioSignature === true) {
    params.set("signature", "1");
  }
  if (includeStudioStamp === false) {
    params.set("stamp", "0");
  } else if (includeStudioStamp === true) {
    params.set("stamp", "1");
  }
  if (options?.layout === "matrix") {
    params.set("layout", "matrix");
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path ?? url;
}

export function pickBrandPrintLocalizedText(
  lang: BrandPrintLanguage,
  lo: string | null | undefined,
  en: string | null | undefined,
): string | null {
  const loText = lo?.trim() ?? "";
  const enText = en?.trim() ?? "";
  if (lang === "en") {
    return enText || loText || null;
  }
  return loText || null;
}
